import {
  appActions,
  CustomerType,
  Country,
  selectCountry,
  selectCustomerType,
  selectFeatureFlags,
  selectLoadedFeatureFlags,
  selectLocale,
  Features,
} from '@buy-viasat/redux/src/app';
import { requestAccessToken, selectAccessToken } from '@buy-viasat/redux/src/auth';
import {
  navActions,
  parseQueryString,
  Routes,
  selectCurrentAppRoute,
  selectMaxRoute,
  selectRoutesStates,
} from '@buy-viasat/redux/src/navigator';
import { selectIsServiceAddressSet, serviceabilityActions } from '@buy-viasat/redux/src/serviceability';
import { RESET_STATE_ACTION } from '@buy-viasat/redux/src/types';
import { Product } from '@buy-viasat/types/build/bv';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes as RouterRoutes, useLocation, useParams } from 'react-router';
import useAutoPublishViewChangesToExperimentationPlatform from 'shared/containers/Analytics/hooks/useAutoPublishViewChangesToExperimentationPlatform';
import { persistor } from '../store';
import { BeamFlowNavigator } from './Flows/BeamFlowNavigator';
import { LoadingSpinner, MaintenanceMode } from '@buy-viasat/react';
import { paymentInformationActions } from '@buy-viasat/redux/src/payment-information';
import { planActions } from '@buy-viasat/redux/src/plan';
import { addressActions } from '@buy-viasat/redux/src/address';
import { transformFeatureFlags } from 'shared/utils/transformFeatureFlags';
import { cartActions } from '@buy-viasat/redux/src/cart';
import { ScrubbedAddress, useScrubAddress } from 'shared/utils/dataFetchingHooks/useScrubAddress';

import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import { useGetFeatureFlags } from 'shared/utils/dataFetchingHooks/useGetFeatureFlags';

const useDefaultLocale = (): string => {
  const { i18n } = useTranslation();
  return i18n.language;
};

export const TrackLocale = ({ location }: { location: { search?: string; hash?: string } }): JSX.Element => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  // useParams returns string | undefined in v7 — guard required before use.
  const { locale } = useParams<{ locale: string }>();
  const currentCountry = useSelector(selectCountry);
  const currentLocale = useSelector(selectLocale);

  if (!locale) {
    return <Navigate replace to={{ ...location, pathname: '/' }} />;
  }

  const validLocale = ((i18n.options?.supportedLngs as string[] | null) ?? ['en-US']).includes(locale);

  if (!validLocale) {
    return <Navigate replace to={{ ...location, pathname: '/' }} />;
  }

  if (currentLocale !== locale) {
    dispatch(RESET_STATE_ACTION);
    dispatch(appActions.setLocale(locale));
    return <Navigate replace to={{ ...location, pathname: '/' }} />;
  }

  const [, country] = locale.split('-');

  const validCountry = (country: Country): boolean => Country[country] !== undefined;
  if (validCountry(country as Country) && country !== currentCountry) {
    dispatch(appActions.setCountry(country as Country));
    return <Navigate replace to={{ ...location, pathname: `/${locale}` }} />;
  } else if (country !== currentCountry) {
    console.error('country does not match store, redirecting home');
    return <Navigate replace to={{ ...location, pathname: '/' }} />;
  } else if (!validCountry(country as Country)) {
    console.error(`Invalid country: ${country}`);
    return <Navigate replace to={{ ...location, pathname: '/' }} />;
  }

  return <></>;
};

export const TrackCustomerType = ({ location }: { location: { search?: string; hash?: string } }): JSX.Element => {
  const dispatch = useDispatch();
  const { locale, customerType } = useParams<{ locale: string; customerType: string }>();
  const currentCustomerType = useSelector(selectCustomerType);
  const currentRoute = useSelector(selectCurrentAppRoute);

  if (!locale || !customerType) {
    return <Navigate replace to={{ ...location, pathname: '/' }} />;
  }

  const isValidCustomerType = (customerType: string): customerType is CustomerType => {
    return Object.values(CustomerType).includes(customerType as CustomerType);
  };
  const hasCustomerPassedPlansPage = (currentRoute: string): boolean => {
    return currentRoute !== Routes.SERVICEABILITY && currentRoute !== Routes.PLAN;
  };

  if (!isValidCustomerType(customerType)) {
    return <Navigate replace to={{ ...location, pathname: `/${locale}` }} />;
  }

  if (currentCustomerType !== customerType) {
    dispatch(planActions.setHavePlansLoaded(false));
    dispatch(planActions.resetSelectedPlan());
    dispatch(navActions.invalidateProgressFrom(Routes.PLAN));
    dispatch(navActions.setupAppRoutes());
    dispatch(
      cartActions.setCartItems({
        plan: {},
        addons: [],
      }),
    );

    dispatch(appActions.resetPartyId());
    dispatch(paymentInformationActions.clearBillingData());
    dispatch(appActions.setCustomerType(customerType));
    dispatch(planActions.setAvailableProducts([]));

    if (hasCustomerPassedPlansPage(currentRoute)) {
      return <Navigate replace to={{ ...location, pathname: `/${locale}/${customerType}/pln` }} />;
    }

    // Preserve route if on PLAN or SERVICEABILITY; navigate to root otherwise.
    if (currentRoute === Routes.PLAN || currentRoute === Routes.SERVICEABILITY) {
      return <Navigate replace to={{ ...location, pathname: `/${locale}/${customerType}/${currentRoute}` }} />;
    }

    return <Navigate replace to={{ ...location, pathname: `/${locale}/${customerType}/` }} />;
  }

  return <></>;
};

export const TrackCurrentStep = (): JSX.Element => {
  const routeStates = useSelector(selectRoutesStates);
  const maxRoute = useSelector(selectMaxRoute);
  const locale = useSelector(selectLocale);
  const customerType = useSelector(selectCustomerType);
  const { pathname } = useLocation();

  const pathArr = pathname.split('/');
  const urlRoute = pathArr[3];

  // If URL has no route or route is not in Routes enum, skip validation (transient state).
  if (!urlRoute || !Object.values(Routes).includes(urlRoute as Routes)) {
    return <></>;
  }

  const urlState = routeStates?.[urlRoute as Routes];

  // Only validate if urlState exists in Redux (route state is initialized).
  if (urlState) {
    const isReachable = urlState.order >= 0 && urlState.order <= (routeStates[maxRoute]?.order ?? -1);

    if (!isReachable) {
      return <Navigate replace to={`/${locale}/${customerType}/${maxRoute}`} />;
    }
  }

  return <></>;
};

// Separate component so useParams() and useLocation() can be called inside the route render.
const LocaleRedirect = (): JSX.Element => {
  const { locale } = useParams<{ locale: string }>();
  const { search } = useLocation();
  if (!locale) return <Navigate replace to="/" />;
  return <Navigate replace to={{ pathname: `/${locale}/${CustomerType.R}/`, search }} />;
};

export const Navigator = (): JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const accessToken = useSelector(selectAccessToken);
  const loadedFeatureFlags = useSelector(selectLoadedFeatureFlags);
  const customerType = useSelector(selectCustomerType);

  const country = useSelector(selectCountry);
  const isServiceAddressSet = useSelector(selectIsServiceAddressSet);

  useEffect(() => {
    if (!accessToken) {
      dispatch(
        requestAccessToken({
          env: process.env['REACT_APP_ENV']!,
          authUrl: process.env['REACT_APP_GRAPHQL_API_URL'] + '/auth',
        }),
      );
    }
    if (!USE_NEW_API && !loadedFeatureFlags && accessToken) {
      dispatch(appActions.retrieveFeatureFlags());
    }
  }, [accessToken, loadedFeatureFlags, dispatch]);

  const { data: featureFlagsFromNewApi } = useGetFeatureFlags();
  const featureFlags = USE_NEW_API ? (featureFlagsFromNewApi as Features) : useSelector(selectFeatureFlags);

  useEffect(() => {
    // reset any offer related data on serviceability reload, before parsing query strings
    dispatch(appActions.setOfferIdParam(''));
  }, []);

  const { scrubAddress } = useScrubAddress<ScrubbedAddress>();

  useEffect(() => {
    if (accessToken && location.search) {
      // pass new scrub address function as param
      parseQueryString(dispatch, location.search, isServiceAddressSet, undefined, scrubAddress);
    }
    dispatch(appActions.startSession(persistor));

    if (accessToken) {
      dispatch(appActions.setCidPhoneMap());
    }
  }, [accessToken, location.search]);

  useEffect(() => {
    // set route based on URL
    const currentRoute = location.pathname.split('/')[3];
    dispatch(navActions.setCurrentAppRoute(currentRoute as Routes));
    dispatch(navActions.setupAppRoutes());
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (window.location?.pathname?.includes(country) && featureFlags) {
      dispatch(serviceabilityActions.setServiceAddressCountryCode(country)); //serviceAddress
      dispatch(serviceabilityActions.setScrubLocationCountryCode(country)); //scrubAddress
      dispatch(addressActions.setShippingCountryCode(country)); //shippingAddress
      dispatch(addressActions.setBillingCountryCode(country)); //billingAddress
      dispatch(appActions.setCountry(country)); //app
      dispatch(appActions.loadFeatureFlags(transformFeatureFlags(featureFlags)));
    }
  }, [country, customerType, location.pathname]);

  // Setup analytics.
  useAutoPublishViewChangesToExperimentationPlatform();

  const onPlanEdit = () => {
    dispatch(serviceabilityActions.resetServiceAddress());
    dispatch(navActions.routeUserTo(Routes.SERVICEABILITY));
  };

  const onPlanSelect = (product: Product) => {
    const isRegulated = product.characteristics.isRegulated === 'true';

    dispatch(
      planActions.planOnNext({
        isRegulated,
        id: product.id,
        name: product.name,
        planPrice: product.price,
        offerId: product.offerId || '',
        planPromo: product.promo?.price || 0,
        isCafII: product.isCafII || false,
        dataCap: product.characteristics.dataCap || '',
        extensionProducts: product.extensionTypes as string[],
        productFamily: product.characteristics.productFamily || '',
        status: product.characteristics.status || '',
        inflectionPointText: product.characteristics.inflectionPointText || '',
        customerType,
      }),
    );
  };

  const onPlanDetails = () => {
    dispatch(navActions.routeUserTo(Routes.PLAN_DETAILS));
  };

  const defaultLocale = useDefaultLocale(); // Will be browser language or default to en-US

  const hasLoadedFeatureFlags = USE_NEW_API ? !!featureFlagsFromNewApi : loadedFeatureFlags;

  return (
    <RouterRoutes>
      <Route path="/" element={<Navigate replace to={{ pathname: `/${defaultLocale}`, search: location.search }} />} />
      <Route
        path={`/${CustomerType.R}`}
        element={
          <Navigate replace to={{ pathname: `/${defaultLocale}/${CustomerType.R}/`, search: location.search }} />
        }
      />
      <Route
        path={`/${CustomerType.B}`}
        element={
          <Navigate replace to={{ pathname: `/${defaultLocale}/${CustomerType.B}/`, search: location.search }} />
        }
      />
      <Route path="/:locale" element={<LocaleRedirect />} />
      {/* * wildcard is required so BeamFlowNavigator's nested <Routes> can match sub-paths. */}
      <Route
        path="/:locale/:customerType/*"
        element={
          <>
            {!hasLoadedFeatureFlags && <LoadingSpinner my="112px" size="large" />}
            <TrackLocale location={location} />
            <TrackCustomerType location={location} />
            <TrackCurrentStep />
            {hasLoadedFeatureFlags &&
              (featureFlags.maintenanceMode ? (
                <MaintenanceMode />
              ) : (
                <BeamFlowNavigator onPlanEdit={onPlanEdit} onPlanDetails={onPlanDetails} onPlanSelect={onPlanSelect} />
              ))}
          </>
        }
      />
      <Route path="*" element={<Navigate replace to={{ pathname: `/${defaultLocale}`, search: location.search }} />} />
    </RouterRoutes>
  );
};
